import { useEffect, useCallback } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { UpdateService } from '../../services/updateService';
import { getPlatformInfo } from '../../utils/platformUtils';
import { logger } from '../../lib/logger';
import {
  updateCheckStateAtom,
  currentVersionAtom,
  latestVersionInfoAtom,
  updateModalVisibleAtom,
  shouldCheckForUpdatesAtom,
  shouldShowUpdateModalAtom,
  updateCheckErrorAtom,
  recordUpdateCheckAtom,
  resetUpdateStateAtom,
  snoozeUpdateReminderAtom,
  snoozeUpdateReminderShortAtom,
  isManualUpdateCheckAtom
} from '../../atoms/v3/updateAtoms';

/**
 * 更新检测 Hook
 * 只在 Android 原生平台启用
 */
export function useUpdateChecker() {
  const [checkState, setCheckState] = useAtom(updateCheckStateAtom);
  const [currentVersion, setCurrentVersion] = useAtom(currentVersionAtom);
  const [latestVersionInfo, setLatestVersionInfo] = useAtom(latestVersionInfoAtom);
  const [checkError, setCheckError] = useAtom(updateCheckErrorAtom);

  const shouldCheck = useAtomValue(shouldCheckForUpdatesAtom);
  const recordCheck = useSetAtom(recordUpdateCheckAtom);
  const resetState = useSetAtom(resetUpdateStateAtom);
  const setIsManualCheck = useSetAtom(isManualUpdateCheckAtom);

  /**
   * 检查是否为支持的平台
   */
  const isSupportedPlatform = useCallback(() => {
    // 支持所有平台的更新检查
    return true;
  }, []);

  /**
   * 执行更新检查
   */
  const checkForUpdates = useCallback(async () => {
    // 只在 Android 原生平台检查更新
    if (!isSupportedPlatform()) {
      logger.debug('跳过更新检查：不是 Android 原生平台');
      return;
    }

    // 检查是否应该进行更新检查
    if (!shouldCheck) {
      logger.debug('跳过更新检查：在暂停期内或检查过于频繁');
      return;
    }

    try {
      setCheckState('checking');
      setCheckError(null);

      logger.info('开始检查应用更新');

      // 根据平台选择更新检查方式
      const platformInfo = getPlatformInfo();
      const result = platformInfo.isElectron
        ? await UpdateService.checkElectronUpdates()
        : await UpdateService.checkForUpdates();

      // 记录检查时间
      recordCheck();

      // 更新当前版本信息
      setCurrentVersion(result.currentVersion);

      if (result.hasUpdate && result.latestVersion) {
        setLatestVersionInfo(result.latestVersion);
        setCheckState('available');
        // 自动检查时重置手动检查标志
        setIsManualCheck(false);

        logger.info('发现可用更新', {
          currentVersion: result.currentVersion,
          latestVersion: result.latestVersion.version
        });
      } else {
        setLatestVersionInfo(null);
        setCheckState('no-update');
        setIsManualCheck(false);

        logger.info('当前已是最新版本', {
          currentVersion: result.currentVersion
        });
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      setCheckError(errorMessage);
      setCheckState('error');
      setIsManualCheck(false);

      logger.error('更新检查失败', error instanceof Error ? error : new Error(String(error)));
    }
  }, [
    isSupportedPlatform,
    shouldCheck,
    setCheckState,
    setCheckError,
    setCurrentVersion,
    setLatestVersionInfo,
    recordCheck,
    setIsManualCheck
  ]);

  /**
   * 手动触发更新检查
   * 在 Electron 环境下强制使用 Web 方式检查，作为原生更新的可靠 fallback
   */
  const manualCheckForUpdates = useCallback(async () => {
    if (!isSupportedPlatform()) {
      return;
    }

    // 手动检查时忽略频率限制
    try {
      setCheckState('checking');
      setCheckError(null);

      // 手动检查时始终使用 Web 方式，确保获取浏览器下载链接作为 fallback
      // 这样在 Electron 环境下，用户可以通过浏览器下载，不依赖 Electron API
      const result = await UpdateService.checkForUpdates();
      recordCheck();

      setCurrentVersion(result.currentVersion);

      if (result.hasUpdate && result.latestVersion) {
        setLatestVersionInfo(result.latestVersion);
        setCheckState('available');
        // 设置手动检查标志，确保 Modal 会显示
        setIsManualCheck(true);
      } else {
        setLatestVersionInfo(null);
        setCheckState('no-update');
        setIsManualCheck(false);
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      setCheckError(errorMessage);
      setCheckState('error');
      setIsManualCheck(false);
    }
  }, [
    isSupportedPlatform,
    setCheckState,
    setCheckError,
    setCurrentVersion,
    setLatestVersionInfo,
    recordCheck,
    setIsManualCheck
  ]);

  return {
    checkState,
    currentVersion,
    latestVersionInfo,
    checkError,
    isSupportedPlatform: isSupportedPlatform(),
    checkForUpdates,
    manualCheckForUpdates,
    resetState
  };
}

/**
 * 更新 Modal 管理 Hook
 */
export function useUpdateModal() {
  const [modalVisible, setModalVisible] = useAtom(updateModalVisibleAtom);
  const shouldShow = useAtomValue(shouldShowUpdateModalAtom);
  const snoozeReminder = useSetAtom(snoozeUpdateReminderAtom);
  const snoozeReminderShort = useSetAtom(snoozeUpdateReminderShortAtom);
  const resetState = useSetAtom(resetUpdateStateAtom);
  const setIsManualCheck = useSetAtom(isManualUpdateCheckAtom);

  // 自动显示 Modal
  useEffect(() => {
    if (shouldShow && !modalVisible) {
      setModalVisible(true);
    }
  }, [shouldShow, modalVisible, setModalVisible]);

  /**
   * 处理立即更新
   */
  const handleUpdateNow = useCallback(async (downloadUrl: string, updateAction: 'download' | 'refresh' | 'testflight' | 'electron') => {
    try {
      switch (updateAction) {
        case 'refresh':
          // PWA/Web - 强制刷新页面
          window.location.reload();
          break;

        case 'testflight':
          // iOS - 打开 TestFlight
          window.open(downloadUrl, '_blank');
          break;

        case 'electron':
          // Electron - 使用原生更新机制
          if (window.electronAPI) {
            logger.info('开始 Electron 原生更新安装');
            await window.electronAPI.downloadAndInstallUpdate();
            // 更新会在下载完成后自动重启应用
          } else {
            // 如果 Electron API 不可用，回退到打开 GitHub 页面
            window.open(downloadUrl, '_blank');
          }
          break;

        case 'download':
        default:
          // Android/其他 - 下载文件
          window.open(downloadUrl, '_blank');
          break;
      }

      // 关闭 Modal，但不记录任何状态
      // 下次启动时如果版本号未变，会继续提醒
      setModalVisible(false);

      // 重置手动检查标志
      setIsManualCheck(false);

      logger.info('用户选择立即更新', { downloadUrl, updateAction });
    } catch (error) {
      logger.error('执行更新操作失败', error instanceof Error ? error : new Error(String(error)));
    }
  }, [setModalVisible, setIsManualCheck]);

  /**
   * 处理稍后提醒
   */
  const handleRemindLater = useCallback((snoozeFor3Days: boolean = false) => {
    if (snoozeFor3Days) {
      // 设置3天暂停期
      snoozeReminder();
      logger.info('用户选择3天内不再提醒');
    } else {
      // 设置1小时暂停期
      snoozeReminderShort();
      logger.info('用户选择1小时内不再提醒');
    }

    // 重置手动检查标志
    setIsManualCheck(false);
  }, [snoozeReminder, snoozeReminderShort, setIsManualCheck]);

  /**
   * 关闭 Modal
   */
  const closeModal = useCallback(() => {
    setModalVisible(false);
    // 重置手动检查标志
    setIsManualCheck(false);
  }, [setModalVisible, setIsManualCheck]);

  return {
    modalVisible,
    handleUpdateNow,
    handleRemindLater,
    closeModal,
    resetState
  };
}
